import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { MaintenanceApiResponse, MaintenanceStatus } from './maintenance.types';

@Injectable({ providedIn: 'root' })
export class MaintenanceService {
    private _httpClient = inject(HttpClient);
    private _document = inject(DOCUMENT);
    private _destroyRef = inject(DestroyRef);
    private _status = signal<MaintenanceStatus>({ enabled: false });
    private _pollingInterval: ReturnType<typeof setInterval> | null = null;
    private _visibilityHandler: (() => void) | null = null;

    readonly status = this._status.asReadonly();
    readonly isActive = computed(() => this._status().enabled);

    async checkMaintenance(): Promise<void> {
        // Allow ?maintenance=true query param to force maintenance mode for testing
        const urlParams = new URLSearchParams(window.location.search);
        const maintenanceParam = urlParams.get('maintenance');
        if (maintenanceParam === 'true') {
            this._status.set({
                enabled: true,
                message: urlParams.get('maintenanceMessage') ?? undefined,
                endDate: urlParams.get('maintenanceEndDate') ?? undefined,
            });
            return;
        }

        try {
            const formData = new FormData();
            const response = await lastValueFrom(
                this._httpClient.post<MaintenanceApiResponse>(
                    'hippo/navigator.php?page=pages/common/MaintenanceScreen.php&page_type=json',
                    formData
                )
            );
            this._status.set({
                enabled: response.result.is_active,
                startDate: response.result.from_datetime,
                endDate: response.result.until_datetime,
            });
        } catch {
            this._status.set({ enabled: false });
        }
    }

    startPolling(): void {
        if (this._pollingInterval) {
            return;
        }

        this._pollingInterval = setInterval(async () => {
            const wasActive = this._status().enabled;
            await this.checkMaintenance();

            if (wasActive && !this._status().enabled) {
                this.stopPolling();
                location.reload();
            }
        }, 60_000);
    }

    startVisibilityCheck(): void {
        if (this._visibilityHandler) {
            return;
        }

        this._visibilityHandler = () => {
            if (this._document.visibilityState === 'visible') {
                this.checkMaintenance();
            }
        };

        this._document.addEventListener('visibilitychange', this._visibilityHandler);

        this._destroyRef.onDestroy(() => this.stopVisibilityCheck());
    }

    stopVisibilityCheck(): void {
        if (this._visibilityHandler) {
            this._document.removeEventListener('visibilitychange', this._visibilityHandler);
            this._visibilityHandler = null;
        }
    }

    stopPolling(): void {
        if (this._pollingInterval) {
            clearInterval(this._pollingInterval);
            this._pollingInterval = null;
        }
    }
}
